/**
 * Easy USA A/B testing framework.
 *
 * Generic assignment/persistence/analytics engine. Contains NO
 * experiment-specific data itself — experiments are declared as JSON in
 * snippets/ab-test-variant.liquid (rendered as a <script type="application/json"
 * id="ab-test-config"> in <head>). Add a new experiment by adding one object
 * to that config; this file never needs to change.
 *
 * Each experiment looks like:
 *   {
 *     "id": "homepage_hero_001",
 *     "allocation": 50,
 *     "variants": { "A": "<shopify section id>", "B": "<shopify section id>" }
 *   }
 *
 * "variants" values are a section's JSON template key (e.g. the key under
 * "sections" in templates/index.json) — NOT the variant label, and NOT the
 * full DOM id of its wrapper div. Shopify renders that wrapper as
 * `shopify-section-<key>` only for sections rendered outside a JSON
 * template; a section that lives in a JSON template gets wrapped as
 * `shopify-section-template--<numeric template id>__<key>` instead, and that
 * numeric id differs per theme/template instance. So both this file and the
 * matching anti-flicker CSS in ab-test-variant.liquid match wrapper ids by
 * *suffix* (`__<key>`, or the bare id as a fallback) instead of hardcoding
 * the full id — see matchSectionSelector() below.
 *
 * This script must load as a plain blocking <script> (no defer/async) placed
 * in <head> before any experiment section markup is parsed, so the
 * data-ab-<experiment id> attribute lands on <html> before the browser paints
 * the page — that's what avoids a flash of the wrong variant.
 *
 * QA/demo override: appending `?ab_<experiment id>=<variant>` to any URL
 * (e.g. `?ab_homepage_hero_001=B`) forces that variant for this pageview and
 * persists it, so it keeps showing on later visits too without needing to
 * keep the param in the URL. Only accepts a variant key that actually exists
 * on the experiment; anything else is ignored.
 */
(function () {
  'use strict';

  var CONFIG_EL = document.getElementById('ab-test-config');
  if (!CONFIG_EL) return;

  var EXPERIMENTS;
  try {
    EXPERIMENTS = JSON.parse(CONFIG_EL.textContent);
  } catch (e) {
    return;
  }
  if (!Array.isArray(EXPERIMENTS) || EXPERIMENTS.length === 0) return;

  var STORAGE_PREFIX = 'eu_ab_';

  function getStoredVariant(experimentId) {
    try {
      return window.localStorage.getItem(STORAGE_PREFIX + experimentId);
    } catch (e) {
      return null;
    }
  }

  function setStoredVariant(experimentId, variant) {
    try {
      window.localStorage.setItem(STORAGE_PREFIX + experimentId, variant);
    } catch (e) {
      // Storage unavailable (private browsing, quota, etc). The visitor still
      // gets a variant for this pageview via the in-memory assignment below,
      // it just won't persist across visits.
    }
  }

  // Matches a section's wrapper div by its JSON template key, regardless of
  // whether Shopify rendered it as `shopify-section-<key>` (sections outside
  // a JSON template) or `shopify-section-template--<id>__<key>` (sections
  // inside one, where <id> varies per theme/template instance).
  function findSectionEl(sectionKey) {
    return document.querySelector(
      '[id="shopify-section-' + sectionKey + '"], [id$="__' + sectionKey + '"]'
    );
  }

  function pickVariant(experiment) {
    var keys = Object.keys(experiment.variants);
    var allocation = typeof experiment.allocation === 'number' ? experiment.allocation : 50;
    var roll = Math.random() * 100;
    return roll < allocation ? keys[0] : keys[1];
  }

  function getUrlOverride(experiment) {
    try {
      var raw = new URLSearchParams(window.location.search).get('ab_' + experiment.id);
      if (!raw) return null;
      var variant = raw.toUpperCase();
      return experiment.variants[variant] ? variant : null;
    } catch (e) {
      return null;
    }
  }

  var api = (window.EasyUSAABTest = window.EasyUSAABTest || {});
  api.assignments = {};

  EXPERIMENTS.forEach(function (experiment) {
    var variant = getUrlOverride(experiment);
    if (variant) {
      setStoredVariant(experiment.id, variant);
    } else {
      variant = getStoredVariant(experiment.id);
      if (!variant || !experiment.variants[variant]) {
        variant = pickVariant(experiment);
        setStoredVariant(experiment.id, variant);
      }
    }
    document.documentElement.setAttribute('data-ab-' + experiment.id, variant);
    api.assignments[experiment.id] = variant;
  });

  api.getVariant = function (experimentId) {
    return api.assignments[experimentId] || null;
  };

  function trackImpressions() {
    EXPERIMENTS.forEach(function (experiment) {
      var variant = api.assignments[experiment.id];
      if (!variant) return;

      var sectionId = experiment.variants[variant];
      var el = findSectionEl(sectionId);
      // Only report an impression if the assigned variant's section actually
      // rendered on this page (e.g. skips firing on pages that don't include
      // this experiment at all).
      if (!el) return;

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'experiment_impression', {
          experiment_id: experiment.id,
          variant: variant,
          non_interaction: true
        });
      }

      document.dispatchEvent(
        new CustomEvent('easyusa:ab:impression', {
          detail: { experiment_id: experiment.id, variant: variant }
        })
      );
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trackImpressions);
  } else {
    trackImpressions();
  }
})();
